import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  GetProductList,
  GetProductDetail,
  CreateProduct,
  UpdateProduct,
  DeleteProduct,
} from "../../Services/CategoryProductService";
import { PAGE_SIZE } from "../../Services/common/const";

export interface ProductState {
  productCollection: any[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  selectedProductDetail: any | null;
  loadingList: boolean;
  loadingDetail: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;
  error: string | null;
  errorDetail: string | null;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  warehouseIds: string;
  cache: Map<string, CacheEntry>;
  lastFetchParams: FetchParams | null;
  createSuccess: boolean;
  updateSuccess: boolean;
  deleteSuccess: boolean;
}

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}

interface FetchParams {
  consumerId: number;
  firmId: number;
  branchId: number;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
  warehouseIds?: string;
  mode: string;
  financialYear: string;
}

const CACHE_DURATION = 5 * 60 * 1000;

const generateCacheKey = (params: FetchParams): string => {
  return `product_list_${params.consumerId}_${params.firmId}_${params.branchId}_${params.pageIndex}_${params.searchValue}_${params.warehouseIds || ""}_${params.mode}`;
};

const isCacheValid = (entry: CacheEntry): boolean => {
  return Date.now() - entry.timestamp < entry.expiresIn;
};

export const fetchProductList = createAsyncThunk(
  "product/fetchProductList",
  async (
    params: FetchParams,
    { rejectWithValue, getState }
  ) => {
    try {
      const root = getState() as { product: ProductState };
      const cacheKey = generateCacheKey(params);

      if (root.product.cache.has(cacheKey)) {
        const cached = root.product.cache.get(cacheKey)!;
        if (isCacheValid(cached)) {
          return cached.data;
        }
      }

      const urlParams = new URLSearchParams();
      urlParams.append("consumerId", params.consumerId.toString());
      urlParams.append("firmId", params.firmId.toString());
      urlParams.append("branchId", params.branchId.toString());
      urlParams.append("pageIndex", params.pageIndex.toString());
      urlParams.append("pageSize", params.pageSize.toString());
      urlParams.append("sortColumn", params.sortColumn);
      urlParams.append("sortDirection", params.sortDirection);
      urlParams.append("searchValue", params.searchValue);
      if (params.warehouseIds) {
        urlParams.append("warehouseIds", params.warehouseIds);
      }
      urlParams.append("mode", params.mode);
      urlParams.append("financialYear", params.financialYear);

      const result: any = await GetProductList(urlParams);

      if (result?.data?.data?.productCollection) {
        return result.data.data;
      }

      return rejectWithValue("No product data received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch products"
      );
    }
  }
);

export const fetchProductDetail = createAsyncThunk(
  "product/fetchProductDetail",
  async (params: Record<string, any>, { rejectWithValue }) => {
    try {
      const urlParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        urlParams.append(key, String(value));
      });

      const result: any = await GetProductDetail(urlParams);

      if (result?.data?.data) {
        return result.data.data;
      }

      return rejectWithValue("No product detail received");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to fetch product details"
      );
    }
  }
);

export const createProductAsync = createAsyncThunk(
  "product/createProduct",
  async (productData: any, { rejectWithValue }) => {
    try {
      const result: any = await CreateProduct(productData);

      if (result?.data) {
        return result.data;
      }

      return rejectWithValue("Failed to create product");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to create product"
      );
    }
  }
);

export const updateProductAsync = createAsyncThunk(
  "product/updateProduct",
  async (productData: any, { rejectWithValue }) => {
    try {
      const result: any = await UpdateProduct(productData);

      if (result?.data) {
        return result.data;
      }

      return rejectWithValue("Failed to update product");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to update product"
      );
    }
  }
);

export const deleteProductAsync = createAsyncThunk(
  "product/deleteProduct",
  async (deleteData: any, { rejectWithValue }) => {
    try {
      const result: any = await DeleteProduct(deleteData);
      if (result?.data || result?.Data) {
        return deleteData;
      }

      return rejectWithValue("Failed to delete product");
    } catch (error: any) {
      return rejectWithValue(
        error?.Message || error?.message || error || "Failed to delete product"
      );
    }
  }
);

const initialState: ProductState = {
  productCollection: [],
  totalCount: 0,
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  selectedProductDetail: null,
  loadingList: false,
  loadingDetail: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,
  error: null,
  errorDetail: null,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  warehouseIds: "",
  cache: new Map(),
  lastFetchParams: null,
  createSuccess: false,
  updateSuccess: false,
  deleteSuccess: false,
};

const productSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    clearProductData: (state) => {
      state.productCollection = [];
      state.totalCount = 0;
      state.selectedProductDetail = null;
      state.error = null;
    },
    clearProductDetail: (state) => {
      state.selectedProductDetail = null;
      state.errorDetail = null;
    },
    clearErrors: (state) => {
      state.error = null;
      state.errorDetail = null;
    },
    setProductCollection: (state, action: PayloadAction<any[]>) => {
      state.productCollection = action.payload;
    },
    clearSuccessFlags: (state) => {
      state.createSuccess = false;
      state.updateSuccess = false;
      state.deleteSuccess = false;
    },
    invalidateCache: (state) => {
      state.cache.clear();
    },
    setFetchMetadata: (state, action: PayloadAction<Partial<FetchParams>>) => {
      state.searchValue = action.payload.searchValue ?? state.searchValue;
      state.sortColumn = action.payload.sortColumn ?? state.sortColumn;
      state.sortDirection = action.payload.sortDirection ?? state.sortDirection;
      state.pageIndex = action.payload.pageIndex ?? state.pageIndex;
      state.warehouseIds = action.payload.warehouseIds ?? state.warehouseIds;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductList.pending, (state, action) => {
        state.loadingList = true;
        state.error = null;
        state.lastFetchParams = action.meta.arg;
      })
      .addCase(fetchProductList.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingList = false;
        state.productCollection = action.payload?.productCollection || [];
        state.totalCount = action.payload?.totalCount || 0;
        state.pageIndex = action.payload?.pageIndex || 1;
        state.pageSize = action.payload?.pageSize || PAGE_SIZE;

        if (state.lastFetchParams) {
          const cacheKey = generateCacheKey(state.lastFetchParams);
          state.cache.set(cacheKey, {
            data: action.payload,
            timestamp: Date.now(),
            expiresIn: CACHE_DURATION,
          });
        }
      })
      .addCase(fetchProductList.rejected, (state, action) => {
        state.loadingList = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchProductDetail.pending, (state) => {
        state.loadingDetail = true;
        state.errorDetail = null;
      })
      .addCase(fetchProductDetail.fulfilled, (state, action: PayloadAction<any>) => {
        state.loadingDetail = false;
        state.selectedProductDetail = action.payload;
      })
      .addCase(fetchProductDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.errorDetail = action.payload as string;
      });

    builder
      .addCase(createProductAsync.pending, (state) => {
        state.loadingCreate = true;
        state.error = null;
        state.createSuccess = false;
      })
      .addCase(createProductAsync.fulfilled, (state) => {
        state.loadingCreate = false;
        state.createSuccess = true;
        state.cache.clear();
      })
      .addCase(createProductAsync.rejected, (state, action) => {
        state.loadingCreate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateProductAsync.pending, (state) => {
        state.loadingUpdate = true;
        state.error = null;
        state.updateSuccess = false;
      })
      .addCase(updateProductAsync.fulfilled, (state) => {
        state.loadingUpdate = false;
        state.updateSuccess = true;
        state.cache.clear();
      })
      .addCase(updateProductAsync.rejected, (state, action) => {
        state.loadingUpdate = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteProductAsync.pending, (state) => {
        state.loadingDelete = true;
        state.error = null;
        state.deleteSuccess = false;
      })
      .addCase(deleteProductAsync.fulfilled, (state) => {
        state.loadingDelete = false;
        state.deleteSuccess = true;
        state.cache.clear();
      })
      .addCase(deleteProductAsync.rejected, (state, action) => {
        state.loadingDelete = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearProductData,
  clearProductDetail,
  clearErrors,
  setProductCollection,
  clearSuccessFlags,
  invalidateCache,
  setFetchMetadata,
} = productSlice.actions;

export default productSlice.reducer;
